import React, { useState, useEffect, useContext, useCallback } from 'react';
import AuthContext from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { elUpperCase } from '../utils/text';
import {
  Box, Typography, Paper, IconButton, Chip, CircularProgress, Alert, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  ToggleButton, ToggleButtonGroup, FormControlLabel, Checkbox, Snackbar,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';
import HotelIcon from '@mui/icons-material/Hotel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DeleteIcon from '@mui/icons-material/Delete';

import { NAV_BG, AMBER, AMBER_DARK } from '../theme';
const GREEN = '#10b981';
const RED = '#ef4444';
const GRAY = '#94a3b8';
const ORANGE = '#f97316';

function fmtDate(d) {
  return d.toLocaleDateString(i18n.language || 'el', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function timeStr(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }
  catch { return null; }
}

function MealCell({ icon, label, active, count, color }) {
  return (
    <Box sx={{
      flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5,
      py: 1.25, px: 0.5, borderRadius: 2, background: '#f8fafc',
    }}>
      {React.cloneElement(icon, { sx: { fontSize: 22, color: active ? color : '#cbd5e1' } })}
      <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', fontWeight: 600, color: '#64748b', textAlign: 'center' }}>
        {label}
      </Typography>
      {active ? (
        <CheckCircleIcon sx={{ fontSize: 20, color }} />
      ) : (
        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '1rem', color: '#cbd5e1' }}>—</Typography>
      )}
      {count > 1 && (
        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.65rem', fontWeight: 700, color }}>
          x{count}
        </Typography>
      )}
    </Box>
  );
}

export default function ParentTodayPage() {
  const { authTokens, backendUrl } = useContext(AuthContext);
  const { t } = useTranslation();

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [date, setDate] = useState(new Date(today));
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Absence dialog state
  const [absenceDialog, setAbsenceDialog] = useState(false);
  const [selectedChild, setSelectedChild] = useState(null);
  const [absenceFrom, setAbsenceFrom] = useState('');
  const [absenceTo, setAbsenceTo] = useState('');
  const [absenceReason, setAbsenceReason] = useState('');
  const [absenceType, setAbsenceType] = useState('full');
  const [skipTo, setSkipTo] = useState(true);
  const [skipFrom, setSkipFrom] = useState(true);
  const [dialogAbsences, setDialogAbsences] = useState([]);
  const [loadingAbsences, setLoadingAbsences] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack] = useState(null);

  const authHeader = { 'Content-Type': 'application/json', Authorization: 'Bearer ' + authTokens?.access };

  const fetchLogs = useCallback(async (dateStr) => {
    const headers = { Authorization: 'Bearer ' + authTokens?.access };
    try {
      const res = await fetch(backendUrl + `api/parent/daily-logs/?date=${dateStr}`, { headers });
      const data = res.ok ? await res.json() : null;
      if (Array.isArray(data)) setLogs(data);
      return data;
    } catch {
      return null;
    }
  }, [authTokens, backendUrl]);

  useEffect(() => {
    setLoading(true);
    setError('');
    fetchLogs(isoDate(date)).then((data) => {
      if (!Array.isArray(data)) setError(t('parent_today.load_error'));
      setLoading(false);
    });
  }, [date]); // eslint-disable-line

  // While viewing today, silently re-fetch every 60 s so bus arrival/departure times
  // appear automatically when the driver finishes the route.
  useEffect(() => {
    if (isoDate(date) !== isoDate(today)) return;
    const id = setInterval(() => fetchLogs(isoDate(date)), 60000);
    return () => clearInterval(id);
  }, [date, fetchLogs]); // eslint-disable-line

  // Re-fetch immediately when an absence is reported/cancelled from another page.
  useEffect(() => {
    const refetch = () => fetchLogs(isoDate(date));
    window.addEventListener('absence-changed', refetch);
    return () => window.removeEventListener('absence-changed', refetch);
  }, [date, fetchLogs]);

  const fetchAbsencesForChild = useCallback(async (childId) => {
    const res = await fetch(backendUrl + `api/parent/absences/?child_id=${childId}`, { headers: authHeader });
    if (res.ok) return res.json();
    return [];
  }, [authTokens, backendUrl]); // eslint-disable-line

  const openAbsenceDialog = async (child) => {
    const todayStr = isoDate(today);
    setSelectedChild(child);
    setAbsenceFrom(todayStr);
    setAbsenceTo(todayStr);
    setAbsenceReason('');
    setAbsenceType('full');
    setSkipTo(true);
    setSkipFrom(true);
    setDialogAbsences([]);
    setAbsenceDialog(true);
    setLoadingAbsences(true);
    const fresh = await fetchAbsencesForChild(child.child_id);
    setDialogAbsences(fresh);
    setLoadingAbsences(false);
  };

  const handleReportAbsence = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(backendUrl + 'api/parent/absence/', {
        method: 'POST', headers: authHeader,
        body: JSON.stringify({
          child_id: selectedChild.child_id,
          start_date: absenceFrom,
          end_date: absenceTo,
          reason: absenceReason,
          absence_type: absenceType === 'full' ? 0 : 1,
          skip_to_school: absenceType === 'bus' ? skipTo : true,
          skip_from_school: absenceType === 'bus' ? skipFrom : true,
        }),
      });
      if (res.ok) {
        setAbsenceFrom(isoDate(today));
        setAbsenceTo(isoDate(today));
        setAbsenceReason('');
        const fresh = await fetchAbsencesForChild(selectedChild.child_id);
        setDialogAbsences(fresh);
        await fetchLogs(isoDate(date));
        window.dispatchEvent(new CustomEvent('absence-changed'));
        setSnack({ severity: 'success', msg: t('parent_children_page.absence_saved') });
      } else {
        const data = await res.json().catch(() => ({}));
        setSnack({ severity: 'error', msg: data.error || t('parent_children_page.absence_failed') });
      }
    } catch {
      setSnack({ severity: 'error', msg: t('common.network_error') });
    }
    setSubmitting(false);
  };

  const handleDeleteAbsence = async (absence) => {
    setDeletingId(absence.absence_id);
    try {
      const res = await fetch(backendUrl + 'api/parent/absence/cancel/', {
        method: 'DELETE', headers: authHeader,
        body: JSON.stringify({ child_id: selectedChild.child_id, date: absence.date }),
      });
      if (res.ok) {
        const updated = dialogAbsences.filter(a => a.absence_id !== absence.absence_id);
        setDialogAbsences(updated);
        await fetchLogs(isoDate(date));
        window.dispatchEvent(new CustomEvent('absence-changed'));
        setSnack({ severity: 'success', msg: t('parent_children_page.absence_deleted') });
      } else {
        setSnack({ severity: 'error', msg: t('parent_children_page.absence_failed') });
      }
    } catch {
      setSnack({ severity: 'error', msg: t('common.network_error') });
    }
    setDeletingId(null);
  };

  const prevDay = () => {
    const d = new Date(date);
    d.setDate(d.getDate() - 1);
    setDate(d);
  };

  const nextDay = () => {
    const d = new Date(date);
    d.setDate(d.getDate() + 1);
    if (d <= today) setDate(d);
  };

  const isToday = isoDate(date) === isoDate(today);

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', p: { xs: 1.5, sm: 3 } }}>
      {/* Date navigation */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 2, sm: 3 },
        background: 'white', borderRadius: 2, px: 1, py: 0.5, border: '1px solid #e2e8f0',
      }}>
        <IconButton onClick={prevDay} size="small" sx={{ color: NAV_BG }}>
          <ChevronLeftIcon />
        </IconButton>
        <Typography sx={{
          fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: { xs: '1rem', sm: '1.1rem' },
          color: NAV_BG, flex: 1, textAlign: 'center',
        }}>
          {fmtDate(date)}
          {isToday && (
            <Chip size="small" label={t('parent_today.today_chip')} sx={{ ml: 1, background: '#dcfce7', color: GREEN, fontFamily: '"Outfit", sans-serif', fontSize: '0.7rem' }} />
          )}
        </Typography>
        <IconButton onClick={nextDay} size="small" disabled={isToday} sx={{ color: isToday ? GRAY : NAV_BG }}>
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress sx={{ color: AMBER }} />
        </Box>
      )}

      {!loading && error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}

      {!loading && !error && logs.length === 0 && (
        <Alert severity="info" sx={{ borderRadius: 2 }}>{t('parent_today.no_data')}</Alert>
      )}

      {!loading && !error && logs.map((child) => (
        <Paper key={child.child_id} sx={{
          mb: 2, borderRadius: 3, overflow: 'hidden',
          border: `1px solid ${
            child.absence_type === 'bus_only' ? 'rgba(217,119,6,0.3)'
              : child.is_absent ? 'rgba(239,68,68,0.3)'
              : '#e2e8f0'
          }`,
          boxShadow: '0 1px 3px rgba(15,23,42,0.08)',
        }}>
          {/* Child header */}
          <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 1.75, background: NAV_BG, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: { xs: 46, sm: 36 }, height: { xs: 46, sm: 36 }, borderRadius: '50%', flexShrink: 0,
              background: `linear-gradient(135deg, ${AMBER} 0%, #D97706 100%)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG, fontSize: { xs: '1.05rem', sm: '0.85rem' },
            }}>
              {child.firstname?.[0]}{child.lastname?.[0]}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: 'white', fontSize: { xs: '1.05rem', sm: '0.95rem' } }}>
                {child.firstname} {child.lastname}
              </Typography>
              <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: { xs: '0.85rem', sm: '0.78rem' }, color: 'rgba(255,255,255,0.6)' }}>
                {child.class_name || ''}
              </Typography>
            </Box>
            {child.is_absent ? (
              child.absence_type === 'bus_only' ? (
                <Chip
                  size="small" label={t('parent_today.no_bus')}
                  sx={{ background: '#fef3c7', color: AMBER_DARK, fontFamily: '"Outfit", sans-serif', fontSize: '0.7rem', fontWeight: 700 }}
                />
              ) : (
                <Chip
                  size="small" label={t('parent_today.absent')}
                  sx={{ background: '#fee2e2', color: RED, fontFamily: '"Outfit", sans-serif', fontSize: '0.7rem', fontWeight: 700 }}
                />
              )
            ) : null}
          </Box>

          <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2 }}>
            {/* Bus times, filling the full card width */}
            <Box sx={{
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1,
              background: '#f8fafc', borderRadius: 2, p: 1.5, mb: 2,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, justifyContent: 'center' }}>
                <DirectionsBusIcon sx={{ color: AMBER, fontSize: 24 }} />
                <Box>
                  <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', color: GRAY, fontWeight: 600, letterSpacing: '0.05em' }}>
                    {elUpperCase(t('parent_today.arrival'))}
                  </Typography>
                  <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG, fontSize: '1.05rem' }}>
                    {timeStr(child.arrival_time) || '—'}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, justifyContent: 'center', borderLeft: '1px solid #e2e8f0' }}>
                <DirectionsBusIcon sx={{ color: '#3b82f6', fontSize: 24 }} />
                <Box>
                  <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', color: GRAY, fontWeight: 600, letterSpacing: '0.05em' }}>
                    {elUpperCase(t('parent_today.departure'))}
                  </Typography>
                  <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG, fontSize: '1.05rem' }}>
                    {timeStr(child.departure_time) || '—'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Meals & nap, evenly spread across the card */}
            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>
              {elUpperCase(t('parent_today.meals'))}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <MealCell icon={<WbSunnyIcon />} label={t('parent_today.breakfast')} active={child.breakfast > 0} count={child.breakfast} color={GREEN} />
              <MealCell icon={<RestaurantIcon />} label={t('parent_today.lunch')} active={child.lunch > 0} count={child.lunch} color={GREEN} />
              <MealCell icon={<LocalCafeIcon />} label={t('parent_today.snack')} active={child.afternoon_snack > 0} count={child.afternoon_snack} color={GREEN} />
              <MealCell icon={<HotelIcon />} label={t('parent_today.nap')} active={child.nap} color="#8b5cf6" />
            </Box>

            {child.notes && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 2, p: 1.5, borderRadius: 2, background: '#f8fafc' }}>
                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem', color: '#64748b', fontStyle: 'italic' }}>
                  {child.notes}
                </Typography>
              </Box>
            )}

            {isToday && (
              <Button
                fullWidth
                variant="contained"
                disableElevation
                startIcon={<EventBusyIcon />}
                onClick={() => openAbsenceDialog(child)}
                sx={{
                  mt: 2, py: 1.25, borderRadius: 2, textTransform: 'none',
                  fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '0.9rem',
                  background: RED, color: 'white',
                  '&:hover': { background: '#dc2626' },
                }}
              >
                {t('parent_today.manage_absences')}
              </Button>
            )}
          </Box>
        </Paper>
      ))}

      {/* Absence management dialog */}
      <Dialog open={absenceDialog} onClose={() => setAbsenceDialog(false)} fullWidth maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG, pb: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {selectedChild
            ? t('parent_children_page.manage_absences_for', { name: selectedChild.firstname })
            : t('parent_children_page.manage_absences')}
          <IconButton size="small" onClick={() => setAbsenceDialog(false)} sx={{ color: '#94a3b8', '&:hover': { color: NAV_BG } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.75 }}>
            {elUpperCase(t('parent_children_page.type_label'))}
          </Typography>
          <ToggleButtonGroup
            value={absenceType} exclusive
            onChange={(_, v) => { if (v) setAbsenceType(v); }}
            size="small" fullWidth
            sx={{ mb: 2, '& .MuiToggleButton-root': { fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', textTransform: 'none', py: 0.75, borderColor: '#e2e8f0', '&.Mui-selected': { background: absenceType === 'full' ? 'rgba(239,68,68,0.1)' : 'rgba(249,115,22,0.1)', color: absenceType === 'full' ? RED : ORANGE, fontWeight: 700, borderColor: absenceType === 'full' ? RED : ORANGE } } }}
          >
            <ToggleButton value="full">{t('parent_children_page.type_full')}</ToggleButton>
            <ToggleButton value="bus">{t('parent_children_page.type_bus')}</ToggleButton>
          </ToggleButtonGroup>

          {absenceType === 'bus' && (
            <Box sx={{ mb: 2, p: 1.5, borderRadius: 1.5, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                {elUpperCase(t('parent_children_page.routes_label'))}
              </Typography>
              {selectedChild?.bus_to_school && (
                <FormControlLabel
                  control={<Checkbox checked={skipTo} onChange={e => setSkipTo(e.target.checked)} size="small" sx={{ color: ORANGE, '&.Mui-checked': { color: ORANGE } }} />}
                  label={<Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem' }}>{t('parent_children_page.skip_to_school')}</Typography>}
                />
              )}
              {selectedChild?.bus_from_school && (
                <FormControlLabel
                  control={<Checkbox checked={skipFrom} onChange={e => setSkipFrom(e.target.checked)} size="small" sx={{ color: ORANGE, '&.Mui-checked': { color: ORANGE } }} />}
                  label={<Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem' }}>{t('parent_children_page.skip_from_school')}</Typography>}
                />
              )}
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <TextField
              fullWidth size="small" type="date" label={t('parent_children_page.from_label')}
              value={absenceFrom} onChange={e => setAbsenceFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, '&.Mui-focused fieldset': { borderColor: AMBER } }, '& label.Mui-focused': { color: AMBER } }}
            />
            <TextField
              fullWidth size="small" type="date" label={t('parent_children_page.to_label')}
              value={absenceTo} onChange={e => setAbsenceTo(e.target.value)}
              inputProps={{ min: absenceFrom }}
              InputLabelProps={{ shrink: true }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, '&.Mui-focused fieldset': { borderColor: AMBER } }, '& label.Mui-focused': { color: AMBER } }}
            />
            <TextField
              fullWidth size="small" label={t('parent_children_page.reason_optional')}
              value={absenceReason} onChange={e => setAbsenceReason(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, '&.Mui-focused fieldset': { borderColor: AMBER } }, '& label.Mui-focused': { color: AMBER } }}
            />
          </Box>

          <Divider sx={{ my: 2 }} />
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>
            {elUpperCase(t('parent_children_page.scheduled'))}
          </Typography>
          {loadingAbsences ? (
            <CircularProgress size={18} sx={{ color: AMBER, display: 'block', mx: 'auto', my: 1 }} />
          ) : dialogAbsences.length === 0 ? (
            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', color: '#94a3b8', fontStyle: 'italic' }}>
              {t('parent_children_page.no_scheduled')}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxHeight: 200, overflowY: 'auto' }}>
              {dialogAbsences.map(a => (
                <Box key={a.absence_id} sx={{
                  display: 'flex', alignItems: 'center', gap: 1, p: '8px 10px', borderRadius: '10px',
                  bgcolor: a.absence_type === 0 ? '#fff5f5' : '#fff7ed',
                  border: `1px solid ${a.absence_type === 0 ? '#fecaca' : '#fed7aa'}`,
                }}>
                  <CalendarMonthIcon sx={{ fontSize: 14, color: a.absence_type === 0 ? RED : ORANGE, flexShrink: 0 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.8rem', color: a.absence_type === 0 ? '#991b1b' : '#92400e' }}>
                      {a.date}{a.reason ? ` — ${a.reason}` : ''}
                    </Typography>
                    {a.created_by_school && (
                      <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.68rem', color: '#94a3b8' }}>
                        {t('parent_children_page.by_school')}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={a.absence_type === 0 ? t('parent_children_page.chip_full') : t('parent_children_page.chip_bus')}
                    size="small"
                    sx={{ height: 16, fontSize: '0.62rem', fontFamily: '"Outfit", sans-serif', fontWeight: 700, background: a.absence_type === 0 ? 'rgba(239,68,68,0.12)' : 'rgba(249,115,22,0.15)', color: a.absence_type === 0 ? RED : ORANGE }}
                  />
                  <IconButton size="small" onClick={() => handleDeleteAbsence(a)} disabled={deletingId === a.absence_id}
                    sx={{ p: 0.5, color: '#94a3b8', '&:hover': { color: RED } }}>
                    {deletingId === a.absence_id
                      ? <CircularProgress size={14} sx={{ color: RED }} />
                      : <DeleteIcon sx={{ fontSize: 14 }} />}
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setAbsenceDialog(false)} sx={{ fontFamily: '"Outfit", sans-serif', color: '#64748b', textTransform: 'none' }}>{t('common.cancel')}</Button>
          <Button
            variant="contained" disableElevation onClick={handleReportAbsence}
            disabled={submitting || !absenceFrom || !absenceTo || (absenceType === 'bus' && !skipTo && !skipFrom)}
            sx={{
              background: absenceType === 'full' ? RED : ORANGE, color: 'white',
              fontFamily: '"Outfit", sans-serif', fontWeight: 700,
              textTransform: 'none', borderRadius: 1.5,
              '&:hover': { background: absenceType === 'full' ? '#dc2626' : '#ea6c0a' },
            }}
          >
            {submitting ? <CircularProgress size={16} sx={{ color: 'white' }} /> : t('parent_children_page.report_btn')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(snack)} autoHideDuration={4000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} sx={{ top: { xs: 'calc(8px + var(--sat))', sm: 'calc(24px + var(--sat))' } }}>
        <Alert severity={snack?.severity || 'info'} onClose={() => setSnack(null)} sx={{ borderRadius: 2 }}>
          {snack?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
